import { useBrowserStorage } from '@aspendental/shared-utils/react';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';

import {
	FacilityFieldsFragment,
	BffBrandType,
	BffFacility,
	BffFacilityFieldsFragment,
	NearbyFacility,
	ReviewWithSummary,
} from '@/graphql/__generated/sdk';

import { getBffFacilityById } from './getBffFacilityById';

import { FACILITY_CODE_STORAGE_KEY, IUser, USA_CENTER_COORDINATES } from '@shared-components/constants';
import { getBffNearByFacilitiesByLocation } from '@shared-components/services/getBffNearByFacilitiesByLocation';
import { Maybe, TAGBrand } from '@shared-components/types';
import { mapFacilities } from '@utils/mapFacilitiesResults';

// Falls back to the center of the USA instead of the (0, 0) null island when no real coordinates are available.
function resolveOrDefaultCoordinates(coords?: { latitude: number; longitude: number }): {
	latitude: number;
	longitude: number;
} {
	return coords?.latitude || coords?.longitude ? coords : USA_CENTER_COORDINATES;
}

export type OfficeInfoType =
	| (FacilityFieldsFragment & {
			attributes?: Exclude<BffFacilityFieldsFragment['attributes'], undefined>;
			email?: string | null;
			specialtyServices?: Exclude<BffFacilityFieldsFragment['specialtyServices'], undefined>;
			reviews?: Maybe<ReviewWithSummary>;
	  })
	| null;
export interface UseFacilitiesParams {
	appName: string;
	appVersion: string;
	brand: TAGBrand;
	bffGraphqlUrl: string;
	useFacilityBeffeDataSource: boolean;
	user?: Maybe<IUser>;
	facilityCode?: string;
	defaultToCdn?: boolean;
	transitionallyUseBeffe?: boolean;
	useSessionStorage?: boolean;
	shouldFetchNearbyFacility?: boolean;
	showSetOfficeButtonInGoogleMapsCards?: boolean;
	fetchAllLocationsOnEmptyResults?: boolean;
}

export type UseFacilitiesResult = {
	officeInfo: OfficeInfoType;
	facilityCode: string | undefined;
	locationState: {
		nearByOfficeLocations: Array<NearbyFacility> | null;
		closestOfficeInfo: NearbyFacility | null;
		distance?: number | null;
		schedulingSystem?: string | null;
		user: IUser;
	};
	facilityBrand: BffBrandType;
	nearbyFacilities: BffFacility[] | undefined | null;
	isWaitingForNearbyFacilities: boolean;
};

type LegacyUseFacilitiesArgs = [
	appName: string,
	appVersion: string,
	brand: TAGBrand,
	bffGraphqlUrl: string,
	useFacilityBeffeDataSource: boolean,
	user?: Maybe<IUser>,
	facilityCode?: string,
	defaultToCdn?: boolean,
	transitionallyUseBeffe?: boolean,
	useSessionStorage?: boolean,
	shouldFetchNearbyFacility?: boolean,
	showSetOfficeButtonInGoogleMapsCards?: boolean,
	fetchAllLocationsOnEmptyResults?: boolean
];

function normalizeUseFacilitiesParams(...args: [UseFacilitiesParams] | LegacyUseFacilitiesArgs): UseFacilitiesParams {
	if (args.length === 1 && typeof args[0] === 'object' && !Array.isArray(args[0])) {
		return args[0];
	}

	const legacyArgs = args as unknown[];
	const appName = legacyArgs[0] as string;
	const appVersion = legacyArgs[1] as string;
	const brand = legacyArgs[2] as TAGBrand;
	const bffGraphqlUrl = legacyArgs[3] as string;
	const useFacilityBeffeDataSource = legacyArgs[4] as boolean;
	const user = legacyArgs[5] as Maybe<IUser> | undefined;
	const facilityCode = legacyArgs[6] as string | undefined;
	const defaultToCdn = legacyArgs[7] as boolean | undefined;
	const transitionallyUseBeffe = legacyArgs[8] as boolean | undefined;
	const useSessionStorage = legacyArgs[9] as boolean | undefined;
	const shouldFetchNearbyFacility = legacyArgs[10] as boolean | undefined;
	const showSetOfficeButtonInGoogleMapsCards = legacyArgs[11] as boolean | undefined;
	const fetchAllLocationsOnEmptyResults = legacyArgs[12] as boolean | undefined;

	return {
		appName,
		appVersion,
		brand,
		bffGraphqlUrl,
		useFacilityBeffeDataSource,
		user,
		facilityCode,
		defaultToCdn,
		transitionallyUseBeffe,
		useSessionStorage,
		shouldFetchNearbyFacility,
		showSetOfficeButtonInGoogleMapsCards,
		fetchAllLocationsOnEmptyResults,
	};
}

const brandThemeToFacilityBrand = (_brand: TAGBrand) => BffBrandType.Lovet;

async function getNearbyFacilitiesForCoordinates(params: {
	coords: { latitude: number; longitude: number };
	brand: TAGBrand;
	bffGraphqlUrl: string;
	appName: string;
	appVersion: string;
	fetchAllLocationsOnEmptyResults?: boolean;
}): Promise<{ facilities: NearbyFacility[]; nearbyFacilities: BffFacility[] | null }> {
	const { coords, brand, bffGraphqlUrl, appName, appVersion, fetchAllLocationsOnEmptyResults } = params;

	const res = await getBffNearByFacilitiesByLocation(
		brandThemeToFacilityBrand(brand).toString() as BffBrandType,
		coords.latitude,
		coords.longitude,
		bffGraphqlUrl,
		appName,
		appVersion,
		{ fetchAllIfNoResults: fetchAllLocationsOnEmptyResults }
	);
	return {
		facilities: mapFacilities(res?.facilityByLocation),
		nearbyFacilities:
			res?.facilityByLocation?.filter((facility): facility is BffFacility => facility !== null) ?? null,
	};
}

function applyNearbyFacilitiesSelection(params: {
	facilities: NearbyFacility[];
	nearbyFacilities: BffFacility[] | null;
	setLocationState: (updater: any) => void;
	setOfficeInfo: (value: OfficeInfoType) => void;
	setValue: (value: string) => void;
	setNearbyFacilities: (value: BffFacility[] | undefined | null) => void;
	removeItem: () => void;
}) {
	const { facilities, nearbyFacilities, setLocationState, setOfficeInfo, setValue, setNearbyFacilities, removeItem } =
		params;

	if (!facilities || facilities.length === 0 || facilities[0].facility === null) {
		removeItem();
		return false;
	}

	setLocationState((prev: any) => ({
		...prev,
		nearByOfficeLocations: facilities,
		closestOfficeInfo: facilities[0],
	}));
	setOfficeInfo(facilities[0].facility);
	setValue(facilities[0].facility.code);
	setNearbyFacilities(nearbyFacilities);

	return true;
}

async function resolvePositionForNearbyFacilities(params: {
	officeInformation?: OfficeInfoType;
	defaultToCdn?: boolean;
	user?: Maybe<IUser>;
}): Promise<{ latitude: number; longitude: number }> {
	const { officeInformation, defaultToCdn, user } = params;

	if (officeInformation?.location?.longitude && officeInformation?.location?.latitude) {
		return {
			latitude: officeInformation.location.latitude as number,
			longitude: officeInformation.location.longitude as number,
		};
	}

	if (defaultToCdn && !officeInformation) {
		return resolveOrDefaultCoordinates(user?.coordinates);
	}

	return await new Promise<{ latitude: number; longitude: number }>((resolve, reject) => {
		globalThis.navigator?.geolocation.getCurrentPosition(
			(position) => {
				resolve({
					latitude: position.coords.latitude,
					longitude: position.coords.longitude,
				});
			},
			reject,
			{ timeout: 10000, maximumAge: 60000 }
		);
	});
}

function shouldUseIpBasedCoordinatesFallback(params: {
	error: unknown;
	defaultToCdn?: boolean;
	user?: Maybe<IUser>;
}): boolean {
	const { error, defaultToCdn, user } = params;
	const geolocationError = error as GeolocationPositionError;
	return Boolean(
		(!defaultToCdn &&
			typeof geolocationError === 'object' &&
			'code' in geolocationError &&
			geolocationError.code === geolocationError.PERMISSION_DENIED &&
			user?.hasIpBasedCoordinates) ||
			(user?.coordinates && user?.hasIpBasedCoordinates)
	);
}

async function resolveNearbyFacilitiesByGeolocation(params: {
	shouldFetchNearbyFacility?: boolean;
	officeInformation?: OfficeInfoType;
	defaultToCdn?: boolean;
	user?: Maybe<IUser>;
	brand: TAGBrand;
	bffGraphqlUrl: string;
	appName: string;
	appVersion: string;
	fetchAllLocationsOnEmptyResults?: boolean;
	removeItem: () => void;
	setNearbyFacilities: (value: BffFacility[] | undefined | null) => void;
	setLocationState: (updater: any) => void;
	setOfficeInfo: (value: OfficeInfoType) => void;
	setValue: (value: string) => void;
}) {
	const {
		shouldFetchNearbyFacility,
		officeInformation,
		defaultToCdn,
		user,
		brand,
		bffGraphqlUrl,
		appName,
		appVersion,
		fetchAllLocationsOnEmptyResults,
		removeItem,
		setNearbyFacilities,
		setLocationState,
		setOfficeInfo,
		setValue,
	} = params;

	if (!shouldFetchNearbyFacility) {
		return;
	}

	const handleError = (error: Error) => {
		console.warn('Error occurred: ', error);
		removeItem();
		setNearbyFacilities(null);
	};

	try {
		const coords = await resolvePositionForNearbyFacilities({ officeInformation, defaultToCdn, user });
		const { facilities, nearbyFacilities } = await getNearbyFacilitiesForCoordinates({
			coords,
			brand,
			bffGraphqlUrl,
			appName,
			appVersion,
			fetchAllLocationsOnEmptyResults,
		});

		setLocationState((prev: any) => ({
			...prev,
			user: {
				hasIpBasedCoordinates: defaultToCdn ? user?.hasIpBasedCoordinates : false,
				coordinates: coords,
			},
		}));
		applyNearbyFacilitiesSelection({
			facilities,
			nearbyFacilities,
			setLocationState,
			setOfficeInfo,
			setValue,
			setNearbyFacilities,
			removeItem,
		});
	} catch (error) {
		if (shouldUseIpBasedCoordinatesFallback({ error, defaultToCdn, user })) {
			console.info('Browser Geolocation is not enabled, using IP based coordinates', user?.coordinates);
			try {
				const coords = resolveOrDefaultCoordinates(user?.coordinates);
				const { facilities, nearbyFacilities } = await getNearbyFacilitiesForCoordinates({
					coords,
					brand,
					bffGraphqlUrl,
					appName,
					appVersion,
					fetchAllLocationsOnEmptyResults,
				});

				setLocationState((prev: any) => ({
					...prev,
					user: {
						hasIpBasedCoordinates: true,
						coordinates: coords,
					},
				}));
				applyNearbyFacilitiesSelection({
					facilities,
					nearbyFacilities,
					setLocationState,
					setOfficeInfo,
					setValue,
					setNearbyFacilities,
					removeItem,
				});
			} catch (err) {
				handleError(err as Error);
			}
		} else {
			handleError(error as Error);
		}
	}
}

export default function useFacilities(params: UseFacilitiesParams): UseFacilitiesResult;
export default function useFacilities(...args: LegacyUseFacilitiesArgs): UseFacilitiesResult;
export default function useFacilities(...args: [UseFacilitiesParams] | LegacyUseFacilitiesArgs) {
	const {
		appName,
		appVersion,
		brand,
		bffGraphqlUrl,
		useFacilityBeffeDataSource,
		user,
		facilityCode,
		defaultToCdn,
		transitionallyUseBeffe,
		useSessionStorage,
		shouldFetchNearbyFacility,
		showSetOfficeButtonInGoogleMapsCards,
		fetchAllLocationsOnEmptyResults,
	} = normalizeUseFacilitiesParams(...args);
	const {
		storageValue: storedFacilityCode,
		setValue,
		removeItem,
	} = useBrowserStorage({
		key: FACILITY_CODE_STORAGE_KEY,
		storage: useSessionStorage ? globalThis.sessionStorage : globalThis.localStorage,
	});

	const storageFacilityCode = storedFacilityCode?.toString();

	const [officeInfo, setOfficeInfo] = useState<OfficeInfoType>(null);
	const [nearbyFacilities, setNearbyFacilities] = useState<BffFacility[] | undefined | null>(undefined);
	const [isWaitingForNearbyFacilities, setIsWaitingForNearbyFacilities] = useState(shouldFetchNearbyFacility);
	const [locationState, setLocationState] = useState<{
		nearByOfficeLocations: Array<NearbyFacility> | null;
		closestOfficeInfo: NearbyFacility | null;
		distance?: number | null;
		schedulingSystem?: string | null;
		user: IUser;
	}>({
		closestOfficeInfo: null,
		nearByOfficeLocations: null,
		distance: null,
		user: {
			hasIpBasedCoordinates: false,
			coordinates: {
				latitude: 0,
				longitude: 0,
			},
		},
	});
	const getNearbyFacilitiesByGeolocation = useCallback(
		async (officeInformation?: OfficeInfoType) => {
			void resolveNearbyFacilitiesByGeolocation({
				shouldFetchNearbyFacility,
				officeInformation,
				defaultToCdn,
				user,
				brand,
				bffGraphqlUrl,
				appName,
				appVersion,
				fetchAllLocationsOnEmptyResults,
				removeItem,
				setNearbyFacilities,
				setLocationState,
				setOfficeInfo,
				setValue,
			});
		},
		[
			shouldFetchNearbyFacility,
			removeItem,
			setValue,
			defaultToCdn,
			user,
			brand,
			bffGraphqlUrl,
			appName,
			appVersion,
			fetchAllLocationsOnEmptyResults,
		]
	);

	const router = useRouter();
	const facilityCodeFromURL = router.isReady
		? new URL(`${globalThis?.location?.origin || 'http://localhost'}${router.asPath}`).searchParams.get('fc')
		: null;
	useEffect(() => {
		// 1. If facility code is passed in the URL, use that to fetch the facility details
		// 2. If 1. failed, and facility code passed from server side props, use that to fetch the facility details
		// 3. if 1. and 2. both failed, and facility code is stored in local storage, use that to fetch the facility details
		// Final fallback: use the geolocation to fetch the facility details
		// new set office btn is introduced in google maps based on feature flag
		// if enabled, set as office btn feature in google maps when used sets the local storage for that facility
		//

		setIsWaitingForNearbyFacilities(true);
		const tempFacilityCode = showSetOfficeButtonInGoogleMapsCards
			? // when showSetOfficeButtonInGoogleMapsCards is true use storageFacilityCode before facilityCode
			  facilityCodeFromURL ?? storageFacilityCode ?? facilityCode ?? ''
			: // when showSetOfficeButtonInGoogleMapsCards is false use facilityCode before storageFacilityCode
			  facilityCodeFromURL ?? facilityCode ?? storageFacilityCode ?? '';

		const getData = async () => {
			if (tempFacilityCode) {
				try {
					let facilityDetails: OfficeInfoType = null;
					if (useFacilityBeffeDataSource || transitionallyUseBeffe) {
						const data = await getBffFacilityById(
							brandThemeToFacilityBrand(brand).toString() as BffBrandType,
							tempFacilityCode,
							bffGraphqlUrl,
							appName,
							appVersion
						);
						const facilities = mapFacilities(data?.facilityById ? [data.facilityById] : null);
						facilityDetails = facilities[0]?.facility ?? null;
					}
					if (facilityDetails) {
						setValue(tempFacilityCode);
						setOfficeInfo(facilityDetails);
						setIsWaitingForNearbyFacilities(false);
						return true;
					}
				} catch (e) {
					console.warn('Error fetching facility by code:', e);
				}
			}
			if (shouldFetchNearbyFacility) {
				await getNearbyFacilitiesByGeolocation();
			}
			setIsWaitingForNearbyFacilities(false);
		};
		void getData();
	}, [
		brand,
		appName,
		appVersion,
		getNearbyFacilitiesByGeolocation,
		storageFacilityCode,
		facilityCodeFromURL,
		setValue,
		removeItem,
		facilityCode,
		useFacilityBeffeDataSource,
		bffGraphqlUrl,
		transitionallyUseBeffe,
		shouldFetchNearbyFacility,
		setIsWaitingForNearbyFacilities,
		showSetOfficeButtonInGoogleMapsCards,
	]);

	useEffect(() => {
		if (officeInfo) {
			if (locationState.nearByOfficeLocations) {
				const isOfficeNearby = locationState.nearByOfficeLocations.some(
					(office) => office.facility.code === officeInfo.code
				);
				if (isOfficeNearby) {
					return;
				}
				setIsWaitingForNearbyFacilities(false);
			} else {
				void getNearbyFacilitiesByGeolocation(officeInfo);
			}
		}
	}, [officeInfo, locationState, getNearbyFacilitiesByGeolocation, setIsWaitingForNearbyFacilities]);

	return {
		officeInfo,
		facilityCode: storageFacilityCode,
		locationState,
		facilityBrand: brandThemeToFacilityBrand(brand),
		nearbyFacilities,
		isWaitingForNearbyFacilities,
	};
}
